import {Cascader, Tag} from 'antd';
import React, {useCallback, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {resetDbFilter, setDbFilter} from '../../../src/redux/actions/filters';
import {getFilters} from '../../../src/selectors/filters';
import {simplify} from '../../../src/util/cmp';
import {useApis} from '../apis/ApisContext';
import {CascaderOption, useCascaderCropOptions} from '../queries/useCascaderCropOptions';
import './CropHierarchicalFilter.css';

const getAllCropIdsForPath = (cascaderOptions: CascaderOption[], path: string[]): string[] => {
  if (!cascaderOptions.length) {
    return [];
  }

  const [categoryValue, subcategoryValue] = path;
  const category = cascaderOptions.find(cat => cat.value == categoryValue);
  if (!category) {
    return [];
  }

  // Level 1 (Category) selection
  if (path.length == 1) {
    const allCropIds: string[] = [];
    for (const subcategory of category.children || []) {
      for (const crop of subcategory.children || []) {
        allCropIds.push(crop.value);
      }
    }

    return allCropIds;
  }
  // Level 2 (Subcategory) selection
  else if (path.length == 2) {
    const subcategory = category.children?.find(sub => sub.value == subcategoryValue);
    return subcategory?.children?.map(crop => crop.value) || [];
  }
  // Level 3 (Individual crop) selection
  else if (path.length == 3) {
    return [path[2]];
  }

  return [];
};

interface CropHierarchicalFilterProps {
  minimized: boolean;
}

export function CropHierarchicalFilter({minimized}: CropHierarchicalFilterProps) {
  const {t} = useApis();
  const {cascaderOptions, isFetching, options} = useCascaderCropOptions(!minimized);
  const dispatch = useDispatch();
  const selectedCropIds = useSelector(getFilters)['crop_id'];
  // HACK: Antd Cascader has a bug where scrolling in search results
  // doesn't work properly in certain cases. This happens only when expandTrigger is set to 'hover'.
  const [isSearchActive, setIsSearchActive] = useState(false);

  const handleChange = useCallback(
    (value: (number | string)[][]) => {
      if (value.some(path => path.some(v => typeof v != 'string'))) {
        console.error('Unexpected value type in CropHierarchicalFilter:', value);
        return;
      }

      if (!value || value.length == 0) {
        dispatch(resetDbFilter('crop_id'));
        return;
      }

      const allCropIds = new Set<string>();
      for (const path of value) {
        const cropIds = getAllCropIdsForPath(cascaderOptions, path as string[]);
        cropIds.forEach(id => allCropIds.add(id));
      }

      const cropIdsArray = Array.from(allCropIds);
      if (cropIdsArray.length > 0) {
        dispatch(setDbFilter({crop_id: cropIdsArray}));
      } else {
        dispatch(resetDbFilter('crop_id'));
      }
    },
    [cascaderOptions, dispatch],
  );

  const handleCloseTag = useCallback(() => {
    dispatch(resetDbFilter('crop_id'));
  }, [dispatch]);

  const handleSearch = useCallback((inputValue: string) => {
    setIsSearchActive(inputValue.length > 0);
  }, []);

  // Convert selected crop IDs to cascader values
  const cascaderValue = useMemo(() => {
    if (!selectedCropIds.length || !cascaderOptions.length) {
      return [];
    }

    const selections: string[][] = [];
    const processedCropIds = new Set<string>();

    for (const category of cascaderOptions) {
      const categoryAllCropIds = new Set<string>();
      for (const subcategory of category.children || []) {
        for (const crop of subcategory.children || []) {
          categoryAllCropIds.add(crop.value);
        }
      }

      const categorySelectedCropIds = selectedCropIds.filter(id => categoryAllCropIds.has(id));
      if (categorySelectedCropIds.length === categoryAllCropIds.size && categoryAllCropIds.size > 0) {
        selections.push([category.value]);
        categorySelectedCropIds.forEach(id => processedCropIds.add(id));
        continue;
      }

      for (const subcategory of category.children || []) {
        const subcategoryAllCropIds = subcategory.children?.map(crop => crop.value) || [];
        const subcategorySelectedCropIds = selectedCropIds.filter(id => subcategoryAllCropIds.includes(id));

        if (subcategorySelectedCropIds.length == subcategoryAllCropIds.length && subcategoryAllCropIds.length > 0) {
          selections.push([category.value, subcategory.value]);
          subcategorySelectedCropIds.forEach(id => processedCropIds.add(id));
        }
      }
    }

    for (const cropId of selectedCropIds) {
      if (processedCropIds.has(cropId)) {
        continue;
      }
      for (const category of cascaderOptions) {
        for (const subcategory of category.children || []) {
          for (const crop of subcategory.children || []) {
            if (crop.value == cropId) {
              selections.push([category.value, subcategory.value, crop.value]);
              break;
            }
          }
        }
      }
    }

    return selections;
  }, [selectedCropIds, cascaderOptions]);

  const minimizedLabel = useMemo(() => {
    if (!selectedCropIds.length || !cascaderOptions.length) {
      return '';
    }

    const labels: string[] = [];
    const processedCropIds = new Set<string>();

    // Check for full category selections
    for (const category of cascaderOptions) {
      const categoryAllCropIds = new Set<string>();
      for (const subcategory of category.children || []) {
        subcategory.children?.forEach(crop => {
          categoryAllCropIds.add(crop.value);
        });
      }

      const categorySelectedCropIds = selectedCropIds.filter(id => categoryAllCropIds.has(id));
      if (categorySelectedCropIds.length == categoryAllCropIds.size && categoryAllCropIds.size > 0) {
        labels.push(`${category.label} (${t('All')})`);
        categorySelectedCropIds.forEach(id => processedCropIds.add(id));
        continue;
      }

      for (const subcategory of category.children || []) {
        const subcategoryAllCropIds = subcategory.children?.map(crop => crop.value) || [];
        const subcategorySelectedCropIds = selectedCropIds.filter(id => subcategoryAllCropIds.includes(id));

        if (subcategorySelectedCropIds.length == subcategoryAllCropIds.length && subcategoryAllCropIds.length > 0) {
          labels.push(`${subcategory.label} (${t('All')})`);
          subcategorySelectedCropIds.forEach(id => processedCropIds.add(id));
        }
      }
    }

    // Add individual crop labels that weren't part of full selections
    const remainingCropIds = selectedCropIds.filter(id => !processedCropIds.has(id));
    const remainingLabels = remainingCropIds.map(cropId => {
      const option = options.find(opt => opt.value === cropId);
      return option?.label || cropId;
    });

    return [...labels, ...remainingLabels].join(', ');
  }, [selectedCropIds, cascaderOptions, t, options]);

  if (minimized) {
    return minimizedLabel ? (
      <Tag closable={true} onClose={handleCloseTag}>
        {minimizedLabel}
      </Tag>
    ) : null;
  }

  return (
    <Cascader
      style={{minWidth: 250}}
      popupClassName="crop-cascader-popup"
      multiple
      expandTrigger={isSearchActive ? 'click' : 'hover'}
      changeOnSelect
      options={cascaderOptions}
      onChange={handleChange}
      value={cascaderValue}
      placeholder={t('harvest_crop')}
      showSearch={{
        filter: (inputValue, path) => {
          return path.some(
            option =>
              option.label && typeof option.label == 'string' && simplify(option.label).includes(simplify(inputValue)),
          );
        },
      }}
      onSearch={handleSearch}
      loading={isFetching}
      allowClear
      maxTagCount="responsive"
      displayRender={labels => labels[labels.length - 1]}
    />
  );
}
